/**
 * Ember theme behaviour. Dependency free.
 * - Sticky header that condenses on scroll (transparent over the homepage hero)
 * - Mobile menu drawer and header dropdowns
 * - Scroll reveal animations (respects prefers-reduced-motion)
 * - Product gallery thumbnails and variant picker
 * - Cart drawer powered by the Ajax Cart API and Section Rendering API
 */

const routes = window.themeRoutes || { cart_add: '/cart/add', cart_change: '/cart/change', root: '/' };
const sectionUrl = (id) => `${routes.root.replace(/\/$/, '')}/?sections=${id}`;

/* ---------- Header ---------- */
(() => {
  const header = document.querySelector('[data-sticky-header]');
  if (!header) return;
  let lastY = window.scrollY;
  let headerHeight = 0;
  let ticking = false;
  const measure = () => {
    const section = header.closest('.shopify-section') || header;
    const rect = section.getBoundingClientRect();
    headerHeight = header.getBoundingClientRect().height;
    const root = document.documentElement.style;
    root.setProperty('--header-height', Math.round(rect.height) + 'px');
    root.setProperty('--drawer-top', Math.round(rect.bottom) + 'px');
  };
  const update = () => {
    ticking = false;
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > 40);
    if (header.dataset.sticky === 'scroll_up') {
      const open = header.querySelector('details[open]');
      header.classList.toggle('is-hidden', y > lastY && y > headerHeight && !open);
    }
    lastY = y;
  };
  requestAnimationFrame(() => {
    measure();
    update();
  });
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true }
  );
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
  else window.addEventListener('resize', measure);
  header.querySelector('.header__drawer')?.addEventListener('toggle', measure);
})();

document.addEventListener('click', (event) => {
  document.querySelectorAll('.header__menu details[open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});

document.querySelectorAll('.header__menu details').forEach((details) => {
  details.addEventListener('toggle', () => {
    if (!details.open) return;
    document.querySelectorAll('.header__menu details[open]').forEach((other) => {
      if (other !== details) other.removeAttribute('open');
    });
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.header details[open]').forEach((details) => {
    details.removeAttribute('open');
    details.querySelector('summary')?.focus();
  });
});

/* ---------- Scroll reveal ---------- */
(() => {
  const items = document.querySelectorAll('[data-reveal]');
  if (!items.length) return;
  if (
    !document.body.classList.contains('animate-reveal') ||
    !('IntersectionObserver' in window) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    items.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  document.documentElement.classList.add('js-reveal');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  items.forEach((el) => observer.observe(el));
})();

/* ---------- Expandable intro text ---------- */
document.querySelectorAll('[data-expandable]').forEach((wrapper) => {
  const button = wrapper.querySelector('[data-expand]');
  const label = wrapper.querySelector('[data-expand-label]');
  if (!button || !label) return;
  const body = wrapper.querySelector('.intro__body');
  // Only offer the toggle when the text is actually being clamped.
  requestAnimationFrame(() => {
    if (body && body.scrollHeight <= body.clientHeight + 4) {
      wrapper.classList.remove('is-collapsed');
      button.hidden = true;
    }
  });
  const more = label.textContent;
  const less = label.dataset.expandLabel;
  button.addEventListener('click', () => {
    const expanded = wrapper.classList.toggle('is-collapsed') === false;
    button.setAttribute('aria-expanded', String(expanded));
    label.textContent = expanded ? less : more;
  });
});

/* ---------- Product gallery ---------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.stacked = this.dataset.layout === 'stacked';
    this.index = 0;
    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.show(index)));
    // swipe between images on touch screens
    let startX = 0;
    let startY = 0;
    this.addEventListener('touchstart', (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true });
    this.addEventListener('touchend', (event) => {
      if (this.stacked || event.target.closest('video, iframe, model-viewer')) return;
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }

  showMedia(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.show(index);
  }

  show(index) {
    const count = this.slides.length;
    if (!count) return;
    index = (index + count) % count;
    this.index = index;
    if (this.stacked) {
      this.slides[index].scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      return;
    }
    this.slides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
    this.slides.forEach((slide, i) => (slide.hidden = i !== index));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------- Variant picker ---------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.addEventListener('change', () => this.onChange());
  }

  onChange() {
    const selected = Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
      const value = fieldset.querySelector('input:checked')?.value ?? fieldset.querySelector('select')?.value;
      const label = fieldset.querySelector('[data-option-value]');
      if (label) label.textContent = value || '';
      return value;
    });
    const variant = this.variants.find((v) => v.options.every((option, i) => option === selected[i]));
    const form = document.getElementById(this.dataset.form);
    const button = form?.querySelector('[type="submit"]');
    const price = document.getElementById(this.dataset.price);

    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = button.dataset.unavailable;
      }
      return;
    }

    form.querySelector('input[name="id"]').value = variant.id;
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.add : button.dataset.soldOut;
    }
    if (price) price.textContent = this.formatMoney(variant.price);

    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, form: this.dataset.form } }));
    if (this.dataset.updateUrl !== 'false') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    if (variant.featured_media) {
      (this.closest('[data-product-section]') || document).querySelector('product-gallery')?.showMedia(variant.featured_media.id);
    }
    const pickup = (this.closest('[data-product-section]') || document).querySelector('[data-pickup]');
    if (pickup) {
      pickup.dataset.variant = variant.id;
      loadPickup(pickup);
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang, {
      style: 'currency',
      currency: this.dataset.currency,
    }).format(cents / 100);
  }
}
customElements.define('variant-picker', VariantPicker);

/* ---------- Cart drawer ---------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-cart-close]')) this.close();
    });
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-line]');
      if (input) this.changeLine(input.dataset.line, input.value);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-line]');
      if (!remove) return;
      event.preventDefault();
      this.changeLine(remove.dataset.removeLine, 0);
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-trigger]');
      if (!trigger) return;
      event.preventDefault();
      this.open();
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[data-product-form]');
      if (!form) return;
      event.preventDefault();
      this.add(form);
    });
  }

  open() {
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    this.dialog.close();
  }

  async add(form) {
    const button = form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${routes.cart_add}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || error.message);
      }
      await this.refresh();
      document.getElementById('QuickViewDialog')?.close();
      this.open();
    } catch (error) {
      // Fall back to the native cart page so the customer never loses the add.
      form.removeAttribute('data-product-form');
      form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  async changeLine(line, quantity) {
    this.setAttribute('aria-busy', 'true');
    await fetch(`${routes.cart_change}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
    });
    await this.refresh();
    this.removeAttribute('aria-busy');
  }

  async refresh() {
    const response = await fetch(sectionUrl('cart-drawer'));
    const sections = await response.json();
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const fresh = html.querySelector('[data-cart-inner]');
    const current = this.querySelector('[data-cart-inner]');
    if (fresh && current) current.replaceWith(fresh);

    const count = html.querySelector('[data-cart-count-source]')?.textContent.trim() || '0';
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === '0';
    });
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Pickup availability ---------- */
function loadPickup(container) {
  if (!container.dataset.variant) return;
  fetch(container.dataset.base + container.dataset.variant)
    .then((response) => (response.ok ? response.text() : ''))
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.pickup');
      container.innerHTML = content ? content.outerHTML : '';
    })
    .catch(() => {
      container.innerHTML = '';
    });
}
document.querySelectorAll('[data-pickup]').forEach(loadPickup);

/* ---------- Auto-submit selects (sort order, country, language) ---------- */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-autosubmit]');
  if (select) select.form?.submit();
});

/* ---------- Predictive search ---------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    this.controller = null;
    this.timer = null;

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-trigger]')) {
        event.preventDefault();
        this.open();
      }
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-search-close]')) this.dialog.close();
    });
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(), 250);
    });
  }

  open() {
    this.dialog.showModal();
    this.input.focus();
  }

  async search() {
    const terms = this.input.value.trim();
    if (!terms) {
      this.results.innerHTML = '';
      return;
    }
    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    try {
      const url = `${routes.predictive_search}?q=${encodeURIComponent(terms)}&section_id=predictive-search&resources[limit]=6&resources[type]=product,collection,page,article`;
      const response = await fetch(url, { signal: this.controller.signal });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-predictive-content]');
      this.results.innerHTML = fresh ? fresh.innerHTML : '';
      if (this.status) this.status.textContent = fresh?.dataset.summary || '';
    } catch (error) {
      if (error.name !== 'AbortError') this.results.innerHTML = '';
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- Prevent images being dragged instead of scrolled ---------- */
document.addEventListener('dragstart', (event) => {
  if (event.target instanceof HTMLImageElement) event.preventDefault();
});

/* ---------- Announcement bar ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.index = 0;
    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(this.index + 1));
    const rotate =
      this.dataset.autorotate === 'true' &&
      this.slides.length > 1 &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (rotate) {
      const interval = Math.max(3, Number(this.dataset.interval) || 5) * 1000;
      this.timer = setInterval(() => this.go(this.index + 1), interval);
      const pause = () => clearInterval(this.timer);
      const resume = () => {
        clearInterval(this.timer);
        this.timer = setInterval(() => this.go(this.index + 1), interval);
      };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('focusin', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusout', resume);
    }
  }

  go(next) {
    const count = this.slides.length;
    this.index = (next + count) % count;
    this.slides.forEach((slide, i) => (slide.hidden = i !== this.index));
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- Slider carousel ---------- */
class SliderCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.index = 0;
    this.slides.forEach((slide) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
    });
    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.buildDots();
    this.observe();
    this.setupAutoplay();
    this.addEventListener('keydown', (event) => {
      if (event.target.closest('input, select, textarea')) return;
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    });
    document.addEventListener('shopify:block:select', (event) => {
      const i = this.slides.indexOf(event.target);
      if (i >= 0) {
        this.pause();
        this.go(i, true);
      }
    });
    this.update();
  }

  buildDots() {
    const dots = this.querySelector('[data-carousel-dots]');
    if (!dots || this.slides.length < 2) return;
    this.dotButtons = this.slides.map((slide, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', (dots.dataset.label || 'Go to slide {n}').replace('{n}', i + 1));
      button.addEventListener('click', () => this.go(i));
      dots.appendChild(button);
      return button;
    });
  }

  observe() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.index = this.slides.indexOf(entry.target);
            this.update();
          }
        });
      },
      { root: this.track, threshold: [0.6] }
    );
    this.slides.forEach((slide) => observer.observe(slide));
  }

  update() {
    const count = this.slides.length;
    this.slides.forEach((slide, i) => {
      slide.dataset.active = String(i === this.index);
      slide.setAttribute('aria-label', (i + 1) + ' / ' + count);
    });
    this.dotButtons?.forEach((button, i) => button.setAttribute('aria-current', String(i === this.index)));
    const status = this.querySelector('[data-carousel-status]');
    if (status) status.textContent = (this.index + 1) + ' / ' + count;
  }

  go(next, instant) {
    const count = this.slides.length;
    this.index = (next + count) % count;
    this.track.scrollTo({ left: this.slides[this.index].offsetLeft - this.track.offsetLeft, behavior: instant || this.reduced ? 'auto' : 'smooth' });
    this.update();
  }

  setupAutoplay() {
    const speed = Number(this.getAttribute('autoplay'));
    const pauseButton = this.querySelector('[data-carousel-pause]');
    if (!speed || this.slides.length < 2 || this.reduced) {
      pauseButton?.setAttribute('hidden', '');
      this.track.setAttribute('aria-live', 'polite');
      return;
    }
    this.speed = speed;
    this.playing = true;
    this.track.setAttribute('aria-live', 'off');
    this.start();
    pauseButton?.addEventListener('click', () => {
      this.playing = !this.playing;
      pauseButton.setAttribute('aria-pressed', String(!this.playing));
      pauseButton.textContent = this.playing ? pauseButton.dataset.pauseLabel : pauseButton.dataset.playLabel;
      this.playing ? this.start() : this.stop();
    });
    ['mouseenter', 'focusin'].forEach((type) => this.addEventListener(type, () => this.stop()));
    ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => this.playing && this.start()));
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.playing && this.start()));
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.go(this.index + 1), this.speed);
  }

  stop() {
    clearInterval(this.timer);
  }

  pause() {
    this.playing = false;
    this.stop();
  }
}
customElements.define('slider-carousel', SliderCarousel);

/* ---------- Video section: load the player only after the click ---------- */
class VideoSection extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('[data-video-play]');
    const template = this.querySelector('template');
    const target = this.querySelector('[data-video-target]');
    if (!button || !template || !target) return;
    button.addEventListener('click', () => {
      target.replaceChildren(template.content.cloneNode(true));
      this.classList.add('is-playing');
      const video = target.querySelector('video');
      if (video) video.play().catch(() => {});
      else target.querySelector('iframe')?.focus();
    });
  }
}
customElements.define('video-section', VideoSection);

/* ---------- Countdown to a real, merchant-set date ---------- */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.end = Date.parse(this.dataset.end);
    if (Number.isNaN(this.end)) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), this.dataset.seconds === 'true' ? 1000 : 30000);
  }

  tick() {
    const left = Math.max(0, this.end - Date.now());
    if (left === 0) {
      clearInterval(this.timer);
      const section = this.closest('.section');
      if (this.dataset.expired === 'hide' && section) section.hidden = true;
      this.classList.add('is-expired');
      return;
    }
    const parts = {
      days: Math.floor(left / 86400000),
      hours: Math.floor((left / 3600000) % 24),
      minutes: Math.floor((left / 60000) % 60),
      seconds: Math.floor((left / 1000) % 60),
    };
    Object.entries(parts).forEach(([unit, value]) => {
      const el = this.querySelector('[data-unit="' + unit + '"]');
      if (el) el.textContent = String(value).padStart(2, '0');
    });
  }
}
customElements.define('countdown-timer', CountdownTimer);

/* ---------- Before / after slider ---------- */
class BeforeAfter extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="range"]');
    if (!input) return;
    const set = () => this.style.setProperty('--position', input.value + '%');
    input.addEventListener('input', set);
    set();
  }
}
customElements.define('before-after', BeforeAfter);

/* ---------- Gallery lightbox ---------- */
class ThemeLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    const scope = this.closest('.section') || document;
    this.items = Array.from(scope.querySelectorAll('[data-lightbox-item]'));
    this.image = this.querySelector('[data-lightbox-image]');
    this.caption = this.querySelector('[data-lightbox-caption]');
    this.counter = this.querySelector('[data-lightbox-counter]');
    this.items.forEach((item, i) => item.addEventListener('click', () => this.open(i)));
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
    });
    this.image.addEventListener('click', (event) => {
      const zoomed = this.image.classList.toggle('is-zoomed');
      this.image.style.transformOrigin = zoomed ? ((event.offsetX / this.image.clientWidth) * 100) + '% ' + ((event.offsetY / this.image.clientHeight) * 100) + '%' : '';
    });
    this.image.addEventListener('mousemove', (event) => {
      if (!this.image.classList.contains('is-zoomed')) return;
      this.image.style.transformOrigin = ((event.offsetX / this.image.clientWidth) * 100) + '% ' + ((event.offsetY / this.image.clientHeight) * 100) + '%';
    });
    this.dialog.addEventListener('close', () => this.image.classList.remove('is-zoomed'));
  }

  open(i) {
    this.show(i);
    this.dialog.showModal();
  }

  show(i) {
    const count = this.items.length;
    this.index = (i + count) % count;
    const item = this.items[this.index];
    this.image.src = item.dataset.src;
    this.image.alt = item.dataset.alt || '';
    if (this.caption) this.caption.textContent = item.dataset.caption || '';
    if (this.counter) this.counter.textContent = (this.index + 1) + ' / ' + count;
  }
}
customElements.define('theme-lightbox', ThemeLightbox);

/* ---------- Shop the look hotspots ---------- */
class ShopTheLook extends HTMLElement {
  connectedCallback() {
    this.pins = Array.from(this.querySelectorAll('[data-hotspot]'));
    this.cards = Array.from(this.querySelectorAll('[data-hotspot-card]'));
    this.pins.forEach((pin) => pin.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggle(pin);
    }));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeAll();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const open = this.pins.find((pin) => pin.getAttribute('aria-expanded') === 'true');
        this.closeAll();
        open?.focus();
      }
    });
    document.addEventListener('shopify:block:select', (event) => {
      const pin = this.pins.find((p) => p.closest('[data-hotspot-wrap]') === event.target);
      if (pin) this.toggle(pin, true);
    });
  }

  toggle(pin, forceOpen) {
    const expanded = pin.getAttribute('aria-expanded') === 'true';
    this.closeAll();
    if (expanded && !forceOpen) return;
    pin.setAttribute('aria-expanded', 'true');
    const card = this.querySelector('#' + pin.getAttribute('aria-controls'));
    if (card) {
      card.hidden = false;
      card.querySelector('a')?.focus({ preventScroll: true });
    }
  }

  closeAll() {
    this.pins.forEach((pin) => pin.setAttribute('aria-expanded', 'false'));
    this.cards.forEach((card) => (card.hidden = true));
  }
}
customElements.define('shop-the-look', ShopTheLook);

/* ---------- Recently viewed products ---------- */
const RECENT_KEY = 'ember:recent';
const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
  } catch (error) {
    return [];
  }
};
document.querySelectorAll('[data-recent-product]').forEach((el) => {
  try {
    const list = readRecent().filter((handle) => handle !== el.dataset.handle);
    list.unshift(el.dataset.handle);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
  } catch (error) {
    /* storage unavailable: the section simply stays hidden */
  }
});

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    const run = () => this.load();
    if ('requestIdleCallback' in window) requestIdleCallback(run);
    else setTimeout(run, 200);
  }

  async load() {
    const limit = Number(this.dataset.limit) || 4;
    const current = this.dataset.exclude;
    const handles = readRecent().filter((handle) => handle !== current).slice(0, limit);
    const grid = this.querySelector('[data-recent-grid]');
    if (!handles.length || !grid) return;
    const cards = await Promise.all(
      handles.map((handle) =>
        fetch('/products/' + handle + '?section_id=product-card-standalone')
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-card]'))
          .catch(() => null)
      )
    );
    cards.filter(Boolean).forEach((card) => {
      const item = document.createElement('li');
      item.append(card);
      grid.append(item);
    });
    if (grid.children.length) this.closest('.section')?.removeAttribute('hidden');
  }
}
customElements.define('recently-viewed', RecentlyViewed);

/* ---------- Generic dialogs: [data-dialog-open="id"], [data-dialog-close] ---------- */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (dialog && !dialog.open) dialog.showModal();
    return;
  }
  const closer = event.target.closest('[data-dialog-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('dialog')) event.target.close();
});

/* ---------- Quantity buttons ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.step(1));
  }

  step(direction) {
    const min = Number(this.input.min) || 0;
    const next = Math.max(min, (Number(this.input.value) || 0) + direction);
    if (next === Number(this.input.value)) return;
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Honest stock note (only for tracked, non-oversellable inventory) ---------- */
class InventoryNote extends HTMLElement {
  connectedCallback() {
    try {
      this.quantities = JSON.parse(this.dataset.quantities);
    } catch (error) {
      return;
    }
    this.render(this.dataset.variant);
    document.addEventListener('variant:change', (event) => this.render(String(event.detail.variant.id)));
  }

  render(id) {
    const quantity = this.quantities[id];
    const threshold = Number(this.dataset.threshold) || 5;
    let text = '';
    if (typeof quantity === 'number' && quantity > 0) {
      if (quantity === 1 && this.dataset.single === 'true') text = this.dataset.singleLabel;
      else if (quantity <= threshold) text = this.dataset.lowLabel.replace('__N__', quantity);
    }
    this.textContent = text;
  }
}
customElements.define('inventory-note', InventoryNote);

/* ---------- Share ---------- */
class ShareButton extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('[data-share]');
    const status = this.querySelector('[data-share-status]');
    button?.addEventListener('click', async () => {
      const data = { title: this.dataset.title, url: this.dataset.url };
      try {
        if (navigator.share) {
          await navigator.share(data);
          return;
        }
        await navigator.clipboard.writeText(data.url);
        if (status) status.textContent = button.dataset.copied || 'Link copied';
        button.querySelector('span').textContent = button.dataset.copied || 'Link copied';
        setTimeout(() => (button.querySelector('span').textContent = button.dataset.label), 2500);
      } catch (error) {
        /* cancelled or unavailable */
      }
    });
    if (button) button.dataset.label = button.querySelector('span').textContent;
  }
}
customElements.define('share-button', ShareButton);

/* ---------- Sticky add-to-cart bar ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const form = document.getElementById(this.dataset.form);
    const target = form?.querySelector('button[type="submit"]');
    this.button = this.querySelector('[data-sticky-button]');
    if (!form || !target || !this.button) return;
    this.hidden = false;
    this.button.addEventListener('click', () => form.requestSubmit());
    const footer = document.querySelector('footer');
    const update = () => {
      const rect = target.getBoundingClientRect();
      const passed = rect.bottom < 0;
      const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      this.classList.toggle('is-visible', passed && !footerVisible);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail.variant;
      this.querySelector('[data-sticky-price]').textContent = new Intl.NumberFormat(document.documentElement.lang, {
        style: 'currency',
        currency: this.dataset.currency,
      }).format(variant.price / 100);
      this.button.disabled = !variant.available;
      this.button.textContent = variant.available ? this.button.dataset.add : this.button.dataset.soldOut;
    });
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ---------- Quick view ---------- */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-quick-view]');
  if (!button) return;
  event.preventDefault();
  const dialog = document.getElementById('QuickViewDialog');
  if (!dialog) {
    window.location.href = button.dataset.quickView;
    return;
  }
  const body = dialog.querySelector('[data-quick-view-content]');
  body.replaceChildren();
  dialog.setAttribute('aria-busy', 'true');
  dialog.showModal();
  try {
    const response = await fetch(button.dataset.quickView + '?section_id=quick-view');
    const html = await response.text();
    const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-view-root]');
    if (!root) throw new Error('empty');
    body.replaceChildren(root);
    root.querySelector('#QuickViewTitle')?.focus?.();
  } catch (error) {
    window.location.href = button.dataset.quickView;
  } finally {
    dialog.removeAttribute('aria-busy');
  }
});

/* ---------- Grid density (2 / 3 / 4 columns), remembered on this device ---------- */
class DensityToggle extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById(this.dataset.target);
    this.buttons = Array.from(this.querySelectorAll('button'));
    let saved = null;
    try {
      saved = localStorage.getItem('ember:grid');
    } catch (error) {
      /* storage unavailable */
    }
    const initial = saved || String(parseInt(this.grid?.style.getPropertyValue('--columns'), 10) || 4);
    this.apply(initial, false);
    this.buttons.forEach((button) => button.addEventListener('click', () => this.apply(button.dataset.columns, true)));
  }

  apply(columns, remember) {
    if (!this.grid) return;
    this.grid.style.setProperty('--columns', columns);
    this.grid.style.setProperty('--columns-mobile', columns === '2' ? '2' : columns === '3' ? '2' : '1');
    this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.columns === columns)));
    if (remember) {
      try {
        localStorage.setItem('ember:grid', columns);
      } catch (error) {
        /* storage unavailable */
      }
    }
  }
}
customElements.define('density-toggle', DensityToggle);

/* ---------- Load more products ---------- */
class LoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-load-more]');
    this.button?.addEventListener('click', () => this.load());
  }

  async load() {
    const grid = document.getElementById(this.dataset.grid);
    if (!grid || !this.dataset.next) return;
    const label = this.button.textContent;
    this.button.disabled = true;
    this.button.textContent = this.button.dataset.loadingLabel;
    try {
      const response = await fetch(this.dataset.next);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.getElementById(this.dataset.grid);
      fresh?.querySelectorAll(':scope > li').forEach((item) => grid.append(item));
      grid.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
      const next = doc.querySelector('load-more[data-next]');
      if (next) {
        this.dataset.next = next.dataset.next;
      } else {
        this.remove();
        return;
      }
    } catch (error) {
      window.location.href = this.dataset.next;
      return;
    }
    this.button.disabled = false;
    this.button.textContent = label;
  }
}
customElements.define('load-more', LoadMore);

/* ---------- Back to top ---------- */
class BackToTop extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    const update = () => this.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.5);
    window.addEventListener('scroll', update, { passive: true });
    update();
    this.button?.addEventListener('click', () => {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
      document.getElementById('MainContent')?.focus({ preventScroll: true });
    });
  }
}
customElements.define('back-to-top', BackToTop);

/* ---------- Cart note (drawer) ---------- */
document.addEventListener('change', (event) => {
  const field = event.target.closest('[data-cart-note]');
  if (!field) return;
  fetch(routes.cart_update + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ note: field.value }),
  }).then(() => {
    const status = field.closest('details')?.querySelector('[data-cart-note-status]');
    if (status) status.textContent = field.dataset.saved;
  });
});
